/**
 * WorkflowIssuesPage
 *
 * Tenant-wide queue of every open / resolved / dismissed
 * ``WorkflowIssue``. Replaces the legacy ``ExceptionsPage`` that
 * read from ``/registrations/rule-exceptions/``; the data source is
 * now ``/workflows/issues/`` so the page works for any resource
 * type the engine raises issues against (currently holdings, soon
 * claims).
 *
 * Issues are auto-raised by workflow blockers or ``raise_issue`` side effects -
 * there is no "create exception" affordance any more. The page just
 * surfaces the queue and routes a click into
 * :class:`WorkflowIssueResolveModal` for resolution.
 */

import { useCallback, useMemo, useState } from 'react'
import { AlertTriangle, Download, Eye, Loader2 } from 'lucide-react'
import { useTableExport } from '@/admin/hooks/use-table-export'

import { AppShell } from '@/admin/components/app-shell'
import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useModal } from '@/shared/components/modals/modal-provider'
import { WorkflowIssueResolveModal } from '@/admin/components/workflow-issues/workflow-issue-resolve-modal'
import { RegistrationResolutionDialog } from '@/admin/components/workflow-issues/registration-resolution-dialog'
import { useWorkflowIssuesPage } from '@/shared/hooks/use-workflows'
import { useTableState } from '@/admin/hooks/use-table-state'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { ProductFilterMenu } from '@/shared/components/ui/product-filter-menu'
import { formatRecordId } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'
import {
  ACTION_REQUEST_ISSUE_KIND,
  TRANSFER_ELIGIBILITY_ISSUE_KIND,
  type WorkflowIssue,
  type WorkflowIssueStatus,
} from '@/shared/lib/workflows/types'

const RESOURCE_PREFIX: Record<string, string> = {
  holding: 'REG',
  claim: 'CLM',
}

type StatusFilter = 'all' | WorkflowIssueStatus

const TABLE_ID = 'workflow-issues'

// Open registration issues share one workspace; a transfer exception sits on
// the registration's workflow but is a transfer decision, and an action
// request is a request to perform one action, so each opens alone.
function opensRegistrationWorkspace(row: WorkflowIssue): boolean {
  return (
    row.status === 'OPEN' &&
    row.resource_type === 'holding' &&
    row.issue_kind !== TRANSFER_ELIGIBILITY_ISSUE_KIND &&
    row.issue_kind !== ACTION_REQUEST_ISSUE_KIND
  )
}

export function WorkflowIssuesPage() {
  const { formatDate } = useFormatters()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN')
  const [search, setSearch] = useState('')
  const { openModal } = useModal()
  // Holdings with open issues open into the full Resolution Workspace
  // dialog (numbered stepper, multi-select pills, claim history).
  // Historical rows - RESOLVED / DISMISSED - fall back to the single-
  // issue detail modal since there's nothing to action in bulk.
  const openResolveDialog = useCallback(
    (holdingUuid: string) =>
      openModal(RegistrationResolutionDialog, { holdingUuid }),
    [openModal],
  )
  const openIssueDetailModal = useCallback(
    (uuid: string) => openModal(WorkflowIssueResolveModal, { uuid }),
    [openModal],
  )

  // The platform's standard page: search, the Filters panel and paging all
  // run on the server, so every exception is reachable however many there are.
  const { page, pageSize, setPage, setPageSize } = useTableState(TABLE_ID)
  const [filterValues, setFilterValues] = useTableFilterPreferences(TABLE_ID)
  const debouncedSearch = useDebouncedValue(search)
  const filters = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      record_type: filterValues.record_type ?? [],
      reason: filterValues.reason ?? [],
      date_preset: filterValues.date_preset?.[0],
    }),
    [statusFilter, debouncedSearch, filterValues],
  )
  const { data, isLoading, isFetching, isError, refetch } = useWorkflowIssuesPage({
    ...filters,
    page,
    page_size: pageSize,
  })
  const issues = data?.results ?? []
  const totalCount = data?.count ?? 0

  const exportParams = useMemo(() => {
    const p: Record<string, string> = {}
    if (filters.status) p.status = filters.status
    if (filters.search) p.search = filters.search
    if (filters.date_preset) p.date_preset = filters.date_preset
    return p
  }, [filters])
  const { exportCsv, isExporting } = useTableExport<WorkflowIssue>({
    endpoint: '/workflows/issues/',
    columns: [],
    columnVisibility: {},
    params: exportParams,
  })

  const columns: DataTableColumn<WorkflowIssue>[] = useMemo(
    () => [
      {
        id: 'created_at',
        header: 'Raised',
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      {
        id: 'record_number',
        header: 'Record #',
        cell: (row) => {
          const num = row.context?.record_number
          const prefix = RESOURCE_PREFIX[row.resource_type] ?? row.resource_type?.slice(0, 3).toUpperCase() ?? 'REC'
          return (
            <span className="font-mono text-[11px] uppercase tracking-[0.02em] text-[var(--ink-2)]">
              {num || formatRecordId(row.resource_id, prefix)}
            </span>
          )
        },
      },
      {
        id: 'resource_type',
        header: 'Type',
        // The tenant's own word, as the Filters panel names it.
        cell: (row) =>
          row.resource_type_label ? (
            <span className="text-[11px] text-[var(--ink-3)]">{row.resource_type_label}</span>
          ) : (
            <span className="text-[11px] capitalize text-[var(--ink-3)]">
              {row.resource_type.replace('_', ' ')}
            </span>
          ),
      },
      {
        id: 'exception_reason',
        header: 'Exception Reason',
        cell: (row) => (
          <span className="max-w-[200px] truncate text-[12px] text-[var(--ink-2)]">
            {row.summary || row.context?.trigger?.title || row.predicate_key || '-'}
          </span>
        ),
      },
      {
        id: 'property_address',
        header: 'Property Address',
        cell: (row) => {
          const addr = row.context?.property_address
          return (
            <span
              className="max-w-[180px] truncate text-[11px] text-[var(--ink-3)]"
              title={addr || undefined}
            >
              {addr || '-'}
            </span>
          )
        },
      },
      {
        id: 'requested_by',
        header: 'Requested By',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-2)]">
            {row.raised_by_user_name || '-'}
          </span>
        ),
      },
      {
        id: 'assigned_to',
        header: 'Assigned To',
        cell: (row) => {
          const name = row.context?.assigned_to_name
          return (
            <span className="text-[12px] text-[var(--ink-2)]">
              {name || '-'}
            </span>
          )
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation()
              if (opensRegistrationWorkspace(row)) {
                openResolveDialog(row.resource_id)
              } else {
                openIssueDetailModal(row.uuid)
              }
            }}
            className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        ),
      },
    ],
    [openResolveDialog, openIssueDetailModal, formatDate],
  )

  return (
    <AppShell>
      <main className="page-container py-10">
        <div
          data-tour="exceptions-list"
          className="mb-6"
        >
          <h1 className="text-[18px] text-[var(--ink)]">
            Exception Requests
          </h1>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            {totalCount} request{totalCount === 1 ? '' : 's'}
          </p>
        </div>

        {/* Toolbar - matches the compact list pattern: tabs on left,
            search + export on right (see ListPageToolbar). */}
        <div className="cv-data-table relative z-10 mb-5 flex flex-wrap items-center justify-between gap-2 md:gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <Tabs
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as StatusFilter)
                setPage(1)
              }}
            >
              <TabsList variant="line">
                <TabsTrigger value="OPEN">Open</TabsTrigger>
                <TabsTrigger value="RESOLVED">Resolved</TabsTrigger>
                <TabsTrigger value="DISMISSED">Dismissed</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ProductFilterMenu
              facets={data?.filter_options ?? []}
              value={filterValues}
              onChange={(values) => {
                setFilterValues(values)
                setPage(1)
              }}
            />
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search exceptions..."
              iconSize={12}
              className="cv-data-table-body w-[120px] rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] py-[5px] pr-2.5 text-[var(--ink)] outline-none transition-[border-color] duration-150 focus:border-[var(--green)] md:w-[200px]"
            />
            <Button
              type="button"
              variant="outline"
              onClick={exportCsv}
              disabled={isExporting}
              className="h-[28px] w-[30px] shrink-0 cursor-pointer rounded-[4px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-transparent p-0 text-[var(--ink-3)] transition-all duration-150 hover:border-[var(--rule-heavy)] hover:bg-transparent hover:text-[var(--ink-2)]"
              title="Download CSV"
            >
              {isExporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>

        <DataTable<WorkflowIssue>
          tableId={TABLE_ID}
          columns={columns}
          data={issues}
          totalCount={totalCount}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={refetch}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          onRowClick={(row) => {
            if (opensRegistrationWorkspace(row)) {
              openResolveDialog(row.resource_id)
            } else {
              openIssueDetailModal(row.uuid)
            }
          }}
          emptyStateNode={
            <EmptyState
              icon={AlertTriangle}
              title="No exceptions"
              description={
                statusFilter === 'OPEN'
                  ? 'Open exceptions will appear here when a workflow guard raises one.'
                  : 'No exceptions match this filter.'
              }
            />
          }
        />
      </main>
    </AppShell>
  )
}
